import React, { useEffect, useMemo, useState } from 'react';

import { Web } from '@mui/icons-material';
import { Form } from 'react-bootstrap';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { PaymentRegisterForm } from './Form';
import PaymentRegisterTable, { paymentRegisterColumns } from './PaymentRegisterTable';
import Excel from '../../../assets/excel.png';
import { Breadcrumbs, Button, EdgeReportingWindow, SubHeader, ReportLoader } from '../../../components';
import { formatIsoToDate, newDateFormatted } from '../../../utils';
import ImageColorChanger from '../../../utils/imageColorChanger';
import getCSVData from '../csv';
import getEdgeReportingMenu from '../getEdgeReportingMenu';
import { userHasAccess, generateReport, getReportData, removeReportData, getList } from '../operations';

import './styles.scoped.css';

// PRST-130: Payment Register (Edge "Historical Cash Application Register by Check#").
// Same report on both brands; the proc derives Company (100 Centerline / 200 PRST) from
// the database it runs against, so the payload carries no brand. Not to be confused with
// 'pay-register-report' (Payroll Check Register).
export const REPORT_ENDPOINT = 'payment-register-report';

const initialForm = {
  FromPaymentDate: '',
  ToPaymentDate: '',
  CustomerName: '',
  BillToNumber: '',
  AMC: '',
};

// Validate on press (SOP: no disabled gating, no alert); returns { fieldName: message }.
export const validateCriteria = (form = {}) => {
  const errors = {};
  if (!form.FromPaymentDate || !form.ToPaymentDate) {
    errors.PaymentDateRange = 'Payment Date Range is required';
  } else if (form.ToPaymentDate < form.FromPaymentDate) {
    errors.PaymentDateRange = 'The To date must be on or after the From date.';
  }
  if (form.BillToNumber !== '' && form.BillToNumber !== undefined && !/^\d+$/.test(String(form.BillToNumber))) {
    errors.BillToNumber = 'Bill To Number must contain numbers only.';
  }
  return errors;
};

// Ticket rule: the Total Payment Applied Amount appears only on the first line where a payment
// has several transactions. The proc returns PaymentRowNumber (1 = first application row of it),
// so the blanking is deterministic regardless of how the user later sorts the table. Applied
// once to the data, so the CSV export mirrors the screen.
export const suppressRepeatPaymentAmounts = (rows = []) =>
  rows.map(row => (Number(row.PaymentRowNumber) === 1 ? row : { ...row, PaymentAmount: '' }));

// Robert (2026-09-05): ~37% of PRST cred_Document_Number values carry an embedded CR/LF.
// Most (680 of 680 checked) are trailing — e.g. '23030\r\n' with nothing after — not a
// duplicate; a minority are a genuine duplicate reading ('23093\r\n23093'). A bare
// replace-with-'' jams a duplicate into one misleading number ('2309323093'); replacing
// with a space instead keeps a real duplicate visibly distinct ('23093 23093') without
// breaking CSV row alignment the way the raw CR/LF did — but left alone it also leaves a
// trailing space on every non-duplicate row ('23030 '), which makes Excel treat the cell as
// text and defeats a lookup against a clean check number. trim() (plus collapsing any other
// whitespace run) removes the trailing/leading case while still separating a real duplicate.
// The proc strips it too (currently also with no separator, see sp_cl_cp_bi_pr.sql); this
// keeps the portal safe against a database where the older proc version is still applied.
// PRST-133 (2026-09-14): the CR/LF test used to gate this, so a value whose only defect was a
// plain leading/trailing space — no CR/LF to detect — passed through untouched and exported as
// '=" 2307"', the same text-not-a-number problem in Excel that the trim above exists to prevent
// (2 of 3,537 rows on a devprst export). Normalising every string value covers both sources; the
// row object is returned untouched when nothing changed, so a clean number keeps its identity.
export const stripLineBreaks = (rows = []) =>
  rows.map(row => {
    if (typeof row.PaymentNumber !== 'string') return row;
    const paymentNumber = row.PaymentNumber.replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ').trim();
    return paymentNumber === row.PaymentNumber ? row : { ...row, PaymentNumber: paymentNumber };
  });

// CSV keeps raw numbers (no currency formatting) and blanks null/undefined/suppressed values
// (getCSVData's default Cell would otherwise render them as the literal string "null"/
// "undefined"). Column Headers carry through as the CSV header row, so the PRST-136 relabels
// need no work here.
//
// PRST-136 (UAT): every other value now exports raw. getCSVData's default Cell — and this
// override until now — wrapped non-numeric values as `=""value""` to stop Excel reinterpreting
// Payment Number/BillTo # as numbers or a date as a locale date (PRST-133 `87483208`). The
// tester read that in the spreadsheet as a live formula on all ten non-numeric columns, so it
// comes off. Dates are formatted here exactly as the screen formats them, because a bare
// MM/DD/YYYY is what Excel parses as a true date — the other half of the same request.
// Trade-off accepted with the ticket: Excel will now read Payment Number and BillTo # as
// numbers, so a check number with a leading zero would lose it (none observed in the 09-11 or
// 09-14 devprst datasets); a space-separated duplicate reading stays text either way.
export const csvColumns = paymentRegisterColumns.map(({ Cell, ...column }) => ({
  ...column,
  Cell: ({ value }) => {
    if (value === null || value === undefined || value === '') return '';
    return column.isDate ? formatIsoToDate(value) : value;
  },
}));

const PaymentRegisterCriteria = ({
  form,
  setForm,
  handleChange,
  handleViewResults,
  errors,
  loadingResults,
  AMCCodeOptions,
  isLoadingAMCCodes,
}) => {
  const navigate = useNavigate();
  return (
    <Form
      noValidate
      onSubmit={e => e.preventDefault()}
      style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <PaymentRegisterForm
        form={form}
        setForm={setForm}
        handleChange={handleChange}
        errors={errors}
        AMCCodeOptions={AMCCodeOptions}
        isLoadingAMCCodes={isLoadingAMCCodes}
      />
      <div className="buttons-row">
        {/* Per SOP, keep the action enabled and validate on press (inline field errors);
            reserve disabled for the in-flight state only. */}
        <Button
          variant="primary"
          disabled={loadingResults}
          onPress={() => handleViewResults()}
          style={{ marginRight: '20px', width: '100px' }}
          text="View"
        />
        <Button
          style={{ width: '100px' }}
          onPress={() => navigate('/edge-reporting')}
          variant="outline"
          text="Cancel"
        />
      </div>
    </Form>
  );
};

const PaymentRegister = () => {
  const { t } = useTranslation();
  const { menuItems } = getEdgeReportingMenu(t);
  const { user } = useSelector(state => state.admin);
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [data, setData] = useState([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [errors, setErrors] = useState({});
  const [AMCCodes, setAMCCodes] = useState([]);
  const [isLoadingAMCCodes, setIsLoadingAMCCodes] = useState(false);

  useEffect(() => {
    if (menuItems) {
      if (!userHasAccess(user, menuItems.find(i => i.to === window.location.pathname)?.id)) navigate('/');
    }
  }, [menuItems]);

  // AMC pick list comes from the existing dqf-apiv2 `amcs` endpoint (sp_cl_cp_bi_amc_list).
  useEffect(() => {
    const loadAMCCodes = async () => {
      setIsLoadingAMCCodes(true);
      const amcs = await getList('amcs');
      setAMCCodes(Array.isArray(amcs) ? amcs : []);
      setIsLoadingAMCCodes(false);
    };
    loadAMCCodes();
  }, []);

  const AMCCodeOptions = useMemo(
    () => AMCCodes.map(({ Code, Description }) => ({ label: Description, value: Code })),
    [AMCCodes],
  );

  const handleChange = e => {
    const { name, value } = e.target;
    // Clear a field's validation error as soon as the user edits it.
    const errorKey = name === 'FromPaymentDate' || name === 'ToPaymentDate' ? 'PaymentDateRange' : name;
    if (errors?.[errorKey]) setErrors(prev => ({ ...prev, [errorKey]: undefined }));
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const fileName = `edge-reporting-payment-register-${newDateFormatted.toString()}.csv`;

  const handleViewResults = async () => {
    const nextErrors = validateCriteria(form);
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    setErrors({});
    setLoadingResults(true);

    // Criteria map 1:1 to sp_cl_cp_bi_pr params. Optional filters are sent as '' / 0 so the
    // proc's "empty means all" guards apply; AMC is the comma-joined multi-select.
    const payload = {
      FromPaymentDate: form.FromPaymentDate,
      ToPaymentDate: form.ToPaymentDate,
      CustomerName: form.CustomerName || '',
      BillToNumber: Number(form.BillToNumber) || 0,
      AMC: form.AMC || '',
    };

    try {
      const result = await generateReport({ endpoint: REPORT_ENDPOINT, payload });
      const rows = result?.location ? await getReportData(result.location) : [];
      if (result?.location) await removeReportData(result.location);

      // getReportData returns undefined on a fetch/parse failure vs [] for a genuinely
      // empty result — don't report a failure to the user as "no data found".
      if (rows === undefined) {
        alert('An error occurred while generating the report. Please try again.');
      } else if (rows?.length) {
        setData(suppressRepeatPaymentAmounts(stripLineBreaks(rows)));
        setShowResults(true);
      } else {
        alert(result?.statusDescription || 'No data found matching your criteria.');
      }
    } catch (error) {
      console.log(error);
      alert('An error occurred while generating the report. Please try again.');
    } finally {
      setLoadingResults(false);
    }
  };

  return (
    <>
      <SubHeader
        text="EDGE Reporting"
        buttons={[{ icon: <Web sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/dashboard') }]}
        popoverTitle="Need help with your Payment Register Report?"
      />
      <div className="header-row">
        <Breadcrumbs
          leftTitle="Reporting Home"
          middleTitle={showResults && 'Payment Register'}
          midItemAction={() => setShowResults(false)}
          rightTitle={showResults ? 'Viewing Report' : 'Payment Register'}
          route="/edge-reporting"
        />
        {!!data?.length && showResults && (
          <div className="export-links">
            <CSVLink filename={fileName} data={getCSVData(data, csvColumns)}>
              <ImageColorChanger src={Excel} className="export-icon" alt="export-payment-register-to-csv" />
            </CSVLink>
          </div>
        )}
      </div>
      {loadingResults && <ReportLoader didAbort={() => setLoadingResults(false)} />}
      {showResults ? (
        <>
          <PaymentRegisterTable data={data} loading={loadingResults} />
          <div className="buttons-row">
            <Button
              variant="primary"
              onPress={() => setShowResults(false)}
              style={{ marginRight: '20px', width: '100px' }}
              text="Back"
            />
            <Button
              style={{ width: '100px' }}
              onPress={() => navigate('/edge-reporting')}
              variant="outline"
              text="Cancel"
            />
          </div>
        </>
      ) : (
        <EdgeReportingWindow
          containerStyle={{ border: '2px solid #343a40', minHeight: 200 }}
          windowContent={
            <PaymentRegisterCriteria
              form={form}
              setForm={setForm}
              handleChange={handleChange}
              handleViewResults={handleViewResults}
              errors={errors}
              loadingResults={loadingResults}
              AMCCodeOptions={AMCCodeOptions}
              isLoadingAMCCodes={isLoadingAMCCodes}
            />
          }
          windowTitle="Criteria"
          headerStyle={{ backgroundColor: '#343a40' }}
        />
      )}
    </>
  );
};

export default PaymentRegister;
